import React from 'react';
import { Button } from '@mantine/core';
import { Container, Title, Text, Stack, Group, Divider } from '@mantine/core';
import { parse, format } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';


// Import pdfmake and its fonts
import pdfMake from "pdfmake/build/pdfmake.js";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import { ContentTable, TDocumentDefinitions } from 'pdfmake/interfaces';
import { useGetApiCallGetAllAnswerForPdf } from '../../api/endpoints';
import {
  AnswerSectionsForPDF,
  AppConfigDTO,
  AttachmentForPdf,
  IFSelectedIndicator,
  IFSelectedOutcome,
  IFSurvey,
  IFSurveyAdditionalProperties
} from '../../api/model';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../hooks/useConfig';
import { isArray } from "lodash";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
const fs = require('fs');

export interface AnswerPdfProps {
  callId?: string;
  subId?: string;
  config: AppConfigDTO
}

export interface QuestionItem {
  name: string;
  title: string | LocalizedLabel;
  inputType?: "number"; // Optional property for input type
}
export interface QuestionChoices {
  value: string;
  text: string | LocalizedLabel;
}
export interface LocalizedLabel {
  default: string;
  it: string | null | undefined;
  en: string | null | undefined;
}
export interface QuestionElement {
  type: "multipletext" | "text" | "radiogroup" | "matrixdropdown" | "checkbox" | "comment" | "dropdown" | "matrix" | "panel" | 'matrixdynamic';
  name: string;
  title: string | LocalizedLabel | null | undefined;
  items?: QuestionItem[]; // Optional property for multipletext questions
  choices?: QuestionChoices[] | any[];
  columns?: QuestionItem[] | QuestionItem[] | any[];
  rows?: QuestionChoices[] | any[];
  showOtherItem?: boolean;
  otherText?: string;
  elements?: QuestionElement[];
  visibleIf?: string;
}

export interface Page {
  name: string;
  elements: QuestionElement[];
}

export interface QuestionsJSON {
  logoPosition: string;
  pages: Page[];
  questionsOnPageMode: string;
}

export interface RenderQuestion {
  title: string;
  questions: any[];
}

const readLabel = (l: any, lang: string, def: string = '') => {
  console.log("readLabel", l)
  if (typeof l === "string") {
    return l;
  }
  if (typeof l === "object" && l !== null) {
    return l[lang] ?? l["default"] ?? def;
  }
  return def;
};

const verifyVisibileIf = (element: QuestionElement, answersJSON: any) => {
  if (element.visibleIf) {
    //   "{tipo_partec} = 'Singola' or {tipo_partec} = 'Associata'",

    const normalizedExpr = element.visibleIf.replace(/=/g, '=='); // Per JS serve '==' o '==='
    const result = evaluateExpression(normalizedExpr, answersJSON);
    return result;
  }
  return true;
}

const evaluateExpression = (expression: string, data: any) => {
  // Sostituisce "=" con "=="
  let expr = expression.replace(/([^=!<>])=([^=])/g, '$1==$2');

  // Normalizza operatori logici
  expr = expr
    .replace(/\bor\b/g, '||')
    .replace(/\band\b/g, '&&')
    .replace(/\bnot\b/g, '!');
  // Estrae tutte le variabili tra parentesi graffe
  const variableRegex = /{(.*?)}/g;
  const variables = [...expr.matchAll(variableRegex)].map(m => m[1]);

  // Sostituisce 'contains' con 'includes' in formato valido JS
  expr = expr.replace(/{(.*?)}\s+contains\s+'(.*?)'/g, (match, variable, value) => {
    const actualValue = data[variable];
    if (Array.isArray(actualValue)) {
      // per array → `data['Relaz_enti'].includes('valore')`
      return `(${JSON.stringify(actualValue)}.includes('${value}'))`;
    } else if (typeof actualValue === 'string') {
      // per stringhe → `data['Relaz_enti'].includes('valore')`
      return `('${actualValue}'.includes('${value}'))`;
    } else {
      return 'false'; // fallback se tipo non supportato
    }
  });

  // Sostituzione delle altre variabili {var} → valore
  variables.forEach((variable) => {
    const value = data[variable];
    if (expr.includes(`{${variable}}`)) {
      const formattedValue = typeof value === 'string' ? `'${value}'` : value;
      const regex = new RegExp(`{${variable}}`, 'g');
      expr = expr.replace(regex, formattedValue);
    }
  });

  // Valutazione finale
  try {
    const result = new Function(`return (${expr});`)();
    if (!result)
      console.log("result", result, "expr", expr, expression, data);
    return result;
  } catch (err) {
    console.error('Errore nella valutazione:', err);
    return false;
  }
}

function getStakeholderName(outcome: IFSelectedOutcome, addProp: IFSurveyAdditionalProperties) {

  if (!outcome.stakeholderName || outcome.stakeholderName == "") {
    var id = outcome.stakeholderId;
    if (addProp.StakeholderType && isArray(addProp.StakeholderType)) {
      var stakeholder = addProp.StakeholderType!.find(x => x.id == id);
      if (stakeholder) {
        return stakeholder.name;
      }

    }
  }


  return outcome.stakeholderName;
}

const AnswerPdf: React.FC<AnswerPdfProps> = ({ callId, subId, config }) => {
  const { t } = useTranslation();

  const renderAttachment = (section: AttachmentForPdf) => {
    let content: any[] = [];
    content.push({
      columns: [
        { text: `${section.attachmentTitle}:`, style: 'question', width: '60%' },
        { text: `${section.fileName}`, style: 'answer', width: '40%' }
      ]
    });
    //content.push({ text: `- ${section.fileName}`, style: 'header1' });
    return content;
  };

  const renderIf = (section: IFSurvey) => {
    let content: any[] = [];
    if (section === null || section === undefined) return content;

    content.push({ text: `${section.additionalProperties!.StepName}`, style: 'header' });
    section.selectedOutcomes!.forEach((outcome: IFSelectedOutcome) => {
      content.push({ text: `${t('export_pdf.outcome')}: ${outcome.outcomeName}`, style: 'question' });
      content.push({ text: `${t('export_pdf.stakeholder')}: ${(getStakeholderName(outcome, section.additionalProperties!))}`, style: 'question' });
      if (outcome.indicators && outcome.indicators.length > 0) {
        content.push({
          columns: [
            { text: `${t('export_pdf.indicator')}`, style: 'question', width: '30%' },
            { text: `${t('export_pdf.target_value')}`, style: 'answer', width: '20%' },
            { text: `${t('export_pdf.proxy')}`, style: 'question', width: '30%' },
            { text: `${t('export_pdf.proxy_value')}`, style: 'answer', width: '20%' }
          ]
        });
        outcome.indicators!.forEach((indicator: IFSelectedIndicator) => {
          content.push({
            columns: [
              { text: `${indicator.indicatorName}:`, style: 'question', width: '30%' },
              { text: `${indicator.indicatorTargetValue}`, style: 'answer', width: '20%' },
              { text: `${indicator.proxyName}:`, style: 'question', width: '30%' },
              { text: `${indicator.proxyValue}`, style: 'answer', width: '20%' }

            ]
          });
        });
      }
      content.push({ canvas: [{ type: 'line', x1: 0, y1: 5, x2: 550, y2: 5, color: '#333' }] }); // Line as a divider
    });

    //content.push({ text: `- ${section.fileName}`, style: 'header1' });
    return content;
  };
  const hadleQuestionElement = (element: QuestionElement, answersJSON: any) => {
    let content: any[] = [];

    console.log("element.name", element.name);
    console.log("element.type", element.type);
    if (!verifyVisibileIf(element, answersJSON)) {
      console.log("Not visible", element, answersJSON);
      return content;
    }
    if (answersJSON[element.name]) {


      if (element.type == 'matrix' && element.rows) {

        // console.log("found  matrix",element,answersJSON[element.name]);


        content.push({ text: `${readLabel(element.title, config.defaultLanguage!, element.name)}`, style: 'header1' });
        element.rows!.map((r: any) => {
          const rval = r.value === undefined ? r : r.value;
          const rtit = r.text === undefined ? r : readLabel(r.text, config.defaultLanguage!);

          const answer = answersJSON[element.name] && answersJSON[element.name][rval];
          if (answer)
            content.push({
              columns: [
                { text: `${rtit}:`, style: 'question', width: '60%' },
                { text: answer ? answer.toString() : '', style: 'answer', width: '*' }
              ]
            });
          content.push({ canvas: [{ type: 'line', x1: 0, y1: 5, x2: 550, y2: 5, color: '#333' }] }); // Line as a divider
        });
      } else if (element.type == 'matrixdynamic') {

        console.log("found element.name", element.name);
        if (answersJSON[element.name]) {
          let ans: any[] = answersJSON[element.name];
          content.push({ text: `${readLabel(element.title, config.defaultLanguage!, element.name)}`, style: 'header1' });

          var tbl: ContentTable = {
            table: {
              headerRows: 1,
              //widths: [ '*', 'auto', 100, '*' ],
              widths: [`${100 / ((element.columns?.length ?? 1) + 1)}%`, ...element.columns!.map(() => {
                // console.log("columns",element.columns?.length,`${100/((element.columns?.length??1)+1)}%`);

                return `${100 / ((element.columns?.length ?? 1) + 1)}%`;
              })],
              body: [
                [
                  // { text: ' ', style: 'tableHeader' },
                  ...element.columns!.map((c: QuestionItem) => {
                    if (c.title === undefined) return { text: `${c.name}`, style: 'tableHeader' };

                    return { text: `${readLabel(c.title, config.defaultLanguage!)}`, style: 'tableHeader' }
                  })
                  // { text: 'Header 1', style: 'tableHeader' },
                  // { text: 'Header 2', style: 'tableHeader' }
                ],

                ...ans.map((rs) => {

                  return [
                    // { text: `${rtit}`, style: 'tableHeaderRow' },
                    ...element.columns!.map((c: QuestionItem) => {
                      // console.log("c.name",c.name);
                      // console.log("element.name",element.name);
                      // console.log("r.value",r.value);
                      // console.log("answersJSON",answersJSON);
                      // console.log("answersJSON[element.name]!",answersJSON[element.name]!);
                      // console.log("answersJSON[element.name]![r.value]!",answersJSON[element.name]![rv]!);
                      // console.log("answersJSON[element.name]![r.value]![c.name]!",answersJSON[element.name]![r.value]![c.name]!);
                      if (rs[c.name]! === undefined) return {};
                      return { text: `${rs[c.name]}`, style: 'tableCell' }
                    })
                  ]
                })

              ]
            }, layout: {
              hLineWidth: function (i, node) {
                // console.log("hLineWidth(i, node)", i, node)
                if (i === 0) return 0;
                return i === node.table.body.length ? 0.2 : 0.2;
              },
              vLineWidth: function (i, node) {
                // console.log("vLineWidth(i, node)", i, node)
                return i === 0 || i === node.table.widths!.length ? 0 : 0;
              },
              hLineColor: function (i, node) {
                return '#333';
              },
              vLineColor: function (i, node) {
                return '#333';
              }
            }
          }
          content.push(tbl);
        }

      } else if (element.type == 'matrixdropdown') {
        console.log("found element.name", element.name);
        content.push({ text: `${readLabel(element.title, config.defaultLanguage!, element.name)}`, style: 'header1' });
        var tbl: ContentTable = {
          table: {
            headerRows: 1,
            //widths: [ '*', 'auto', 100, '*' ],
            widths: [`${100 / ((element.columns?.length ?? 1) + 1)}%`, ...element.columns!.map(() => {
              // console.log("columns",element.columns?.length,`${100/((element.columns?.length??1)+1)}%`);

              return `${100 / ((element.columns?.length ?? 1) + 1)}%`;
            })],
            //widths: ['auto', ...element.columns!.map(() => "auto")],
            body: [

              // Header row with borders
              [
                { text: ' ', style: 'tableHeader' },
                ...element.columns!.map((c: QuestionItem) => {
                  if (c.title === undefined) return { text: `${c.name}`, style: 'tableHeader' };

                  return { text: `${readLabel(c.title, config.defaultLanguage!)}`, style: 'tableHeader' }
                })
                // { text: 'Header 1', style: 'tableHeader' },
                // { text: 'Header 2', style: 'tableHeader' }
              ],

              // Data rows with borders
              ...element.rows!.map((rs: QuestionChoices | string) => {
                var rtit: string;
                var rv: string;
                if (typeof rs === 'string') {
                  rtit = rs
                  rv = rs
                } else {
                  let r: QuestionChoices = rs;
                  rtit = readLabel(r.text, config.defaultLanguage!)
                  rv = r.value
                  if (rtit === undefined) rtit = r.value
                  if (rtit === undefined) rtit = `${r}`
                  if (rv === undefined) rv = `${r}`

                }

                // console.log("r.text",r.text);
                // console.log("r.value",r.value);
                // console.log("r",r);
                // console.log("element.name",element.name);

                return [
                  { text: `${rtit}`, style: 'tableHeaderRow' },
                  ...element.columns!.map((c: QuestionItem) => {
                    // console.log("c.name",c.name);
                    // console.log("element.name",element.name);
                    // console.log("r.value",r.value);
                    // console.log("answersJSON",answersJSON);
                    // console.log("answersJSON[element.name]!",answersJSON[element.name]!);
                    // console.log("answersJSON[element.name]![r.value]!",answersJSON[element.name]![rv]!);
                    // console.log("answersJSON[element.name]![r.value]![c.name]!",answersJSON[element.name]![r.value]![c.name]!);
                    if (answersJSON[element.name]! === undefined || answersJSON[element.name]![rv]! === undefined || answersJSON[element.name]![rv]![c.name]! === undefined) return {};
                    return { text: `${answersJSON[element.name]![rv]![c.name]!}`, style: 'tableCell' }
                  })
                ]
              })


            ],

          }, layout: {
            hLineWidth: function (i, node) {
              // console.log("hLineWidth(i, node)", i, node)
              if (i === 0) return 0;
              return i === node.table.body.length ? 0.2 : 0.2;
            },
            vLineWidth: function (i, node) {
              // console.log("vLineWidth(i, node)", i, node)
              return i === 0 || i === node.table.widths!.length ? 0 : 0;
            },
            hLineColor: function (i, node) {
              return '#333';
            },
            vLineColor: function (i, node) {
              return '#333';
            }
          }
        }
        //console.log("tbl",tbl);

        content.push(tbl);

      } else if (element.type == 'radiogroup') {
        const selectedValue: string = answersJSON[element.name];
        const choicesMap = new Map<string, string>();
        const isOtherSelected = selectedValue == 'other';

        if (element.choices) {
          element.choices.forEach((choice) => {
            const value = typeof choice === 'string' ? choice : choice.value;
            const label = typeof choice === 'string' ? choice : readLabel(choice.text, config.defaultLanguage!);
            choicesMap.set(value, label);
          });
        }

        let readableAnswers = choicesMap.get(selectedValue) ?? selectedValue;
        if (selectedValue === 'other' && isOtherSelected) {
          const otherComment = answersJSON[`${element.name}-Comment`] || '';
          readableAnswers = `${readLabel(element.otherText, config.defaultLanguage!, 'Altro')}: ${otherComment}`;
        }


        const toret = {
          columns: [
            { text: `${readLabel(element.title, config.defaultLanguage!, element.name)}:`, style: 'question', width: '60%' },
            { text: readableAnswers, style: 'answer', width: 'auto' }
          ]
        };

        content.push(toret);
      } else if (element.type == 'checkbox') {
        const selectedValues: string[] = answersJSON[element.name] || [];
        const isOtherSelected = selectedValues.includes('other');
        const choicesMap = new Map<string, string>();

        if (element.choices) {
          element.choices.forEach((choice) => {
            const value = typeof choice === 'string' ? choice : choice.value;
            const label = typeof choice === 'string' ? choice : readLabel(choice.text, config.defaultLanguage!);
            choicesMap.set(value, label);
          });
        }

        let readableAnswers = selectedValues.map((val) => {
          if (val === 'other' && isOtherSelected) {
            const otherComment = answersJSON[`${element.name}-Comment`] || '';
            return `${readLabel(element.otherText, config.defaultLanguage!, 'Altro')}: ${otherComment}`;
          }
          return choicesMap.get(val) ?? val;
        });

        const toret = {
          columns: [
            { text: `${readLabel(element.title, config.defaultLanguage!, element.name)}:`, style: 'question', width: '60%' },
            { text: readableAnswers.join(', '), style: 'answer', width: 'auto' }
          ]
        };

        content.push(toret);
      } else if (element.items) {
        content.push({ text: `${readLabel(element.title, config.defaultLanguage!, element.name)}`, style: 'header1' });
        element.items.forEach((item: { title: string | LocalizedLabel | null | undefined; name: string; }) => {
          const answer = answersJSON[element.name] && answersJSON[element.name][item.name];
          if (answer) {

            // console.log("answer",answer);
            // console.log("item",item);
            content.push({
              columns: [
                { text: `${readLabel(item.title, config.defaultLanguage!)}:`, style: 'question', width: '60%' },
                { text: answer ? answer.toString() : '', style: 'answer', width: 'auto' }
              ]
            });
          }
          content.push({ canvas: [{ type: 'line', x1: 0, y1: 5, x2: 550, y2: 5, color: '#333' }] }); // Line as a divider
        });
      } else if (element.type == 'panel') {
        console.log("panel", element)

      } else {
        const answer = answersJSON[element.name] ?? '';
        var otherAnswer = '';

        if (element.showOtherItem && answer === 'other') {
          otherAnswer = `${readLabel(element.otherText, config.defaultLanguage!, t('export_pdf.other'))}: ${answersJSON[`${element.name}-Comment`]}`;
        }
        // console.log("answer",answer);
        // console.log("otherAnswer",otherAnswer);

        content.push({
          columns: [
            { text: `${readLabel(element.title, config.defaultLanguage!, element.name)}:`, style: 'question', width: '60%' },
            { text: answer === 'other' ? otherAnswer : answer.toString(), style: 'answer', width: 'auto' }
          ]
        });


        // content.push({ text: answer ? answer.toString() : '' });
      }

      // Add a divider after each question
      // content.push({ canvas: [{ type: 'line', x1: 0, y1: 5, x2: 550, y2: 5 }] }); // Line as a divider
      content.push({ text: ' ', style: 'blankLine' });
    }
    else if (element.type == 'panel') {
      console.log("panel", element)
      content.push({ text: `${readLabel(element.title, config.defaultLanguage!, element.name)}`, style: 'header1' });
      if (element.elements) {

        element.elements!.forEach((innerelement: any) => {
          let ret = hadleQuestionElement(innerelement, answersJSON);
          content.push(...ret);
        })

      }
    }

    return content;
  };
  const renderQuestions = (section: AnswerSectionsForPDF) => {
    let content: any[] = [];
    if (section.questions == null || section.questions == "") return null;
    let questions: QuestionsJSON = JSON.parse(section.questions!);
    let answersJSON = section.answers != "" ? JSON.parse(section.answers!) : [];
    console.log("questions", questions.pages[0].elements);
    // console.log("answersJSON",answersJSON);
    questions.pages[0].elements.forEach((element: QuestionElement) => {
      let contentElement: any[] = hadleQuestionElement(element, answersJSON);
      content.push(...contentElement);
    }
    );
    content.push({ canvas: [{ type: 'line', x1: 0, y1: 5, x2: 550, y2: 5 }] }); // Line as a divider

    return { title: section.sectionName!, questions: content };
  };
  // const questionsJSON: QuestionsJSON = {"logoPosition":"right","pages":[{"name":"page1","elements":[{"type":"multipletext","name":"question1","title":"Please write the number of male, female or nonbinary persons being founders of the company. ","items":[{"name":"text1","title":"Male"},{"name":"text2","title":"Female"},{"name":"text3","title":"Nonbinary"}]},{"type":"multipletext","name":"question6","title":"Please write the number of male, female or nonbinary persons having managerial positions in the company.  This may include directors, general managers, board members, etc.","items":[{"name":"text1","title":"Male"},{"name":"text2","title":"Female"},{"name":"text3","title":"Nonbinary"}]},{"type":"multipletext","name":"question2","title":"Please write the total number of male employees in the company","items":[{"name":"full-time","inputType":"number","title":"Full Time"},{"name":"part-time","inputType":"number","title":"Part Time"},{"name":"intern","inputType":"number","title":"Intern / Trainees"},{"name":"volunteer","inputType":"number","title":"Volunteer"}]},{"type":"multipletext","name":"question3","title":"Please write the total number of female employees in the company","items":[{"name":"full-time","inputType":"number","title":"Full Time"},{"name":"part-time","inputType":"number","title":"Part Time"},{"name":"intern","inputType":"number","title":"Intern / Trainees"},{"name":"volunteer","inputType":"number","title":"Volunteer"}]},{"type":"multipletext","name":"question4","title":"Please write the total number of nonbinary employees in the company","items":[{"name":"full-time","inputType":"number","title":"Full Time"},{"name":"part-time","inputType":"number","title":"Part Time"},{"name":"intern","inputType":"number","title":"Intern / Trainees"},{"name":"volunteer","inputType":"number","title":"Volunteer"}]},{"type":"text","name":"question5","title":"Please describe if the company has any formal collaborations with other actors. This could be for example an incubator, research centers, public agencies, or if the company is a spin out from a university or another company."}]}],"questionsOnPageMode":"singlePage"};

  // const answersJSON: any = {"question1":{"text1":"2","text2":"1"},"question6":{"text1":"3","text2":"2"},"question3":{"full-time":9,"part-time":1,"intern":1},"question2":{"full-time":12,"intern":1},"question5":"Company is a spin out from Auckland University and today we have collaboration with public research and we are part of different ecosystem regional in France, national and Europe (around 5 ongoing collaborations) "};
  const { data: fullAnswerForPDF } = useGetApiCallGetAllAnswerForPdf({ callId: callId, subId: subId })


  const formatSubmissionDateTime = (dateString: string | undefined): string => {
    if (!dateString) return '';
    if (dateString == '') return '';
    var parsedDate;
    if (dateString.includes("AM") || dateString.includes("PM")) {
      parsedDate = parse(dateString, 'M/d/yyyy h:mm:ss a', new Date());

    } else {
      parsedDate = parse(dateString, 'dd/MM/yyyy HH:mm:ss', new Date());
    }

    const utcDate = zonedTimeToUtc(parsedDate, 'UTC');

    const ret = new Intl.DateTimeFormat(navigator.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }).format(utcDate);
    return ret;
  }

  const generatePDF = () => {

    console.log("fullAnswerForPDF", fullAnswerForPDF)

    // Read the image file and convert it to base64
    const dataURL = fullAnswerForPDF?.configLogo ?? `data:image/png;base64,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`;

    var all = fullAnswerForPDF?.sectionsForPDF?.map((e) => { return renderQuestions(e) })
    var attachments = fullAnswerForPDF?.attachments?.map((e) => { return renderAttachment(e) })
    var fullAttachments = [] as any[];
    if (attachments && attachments?.length > 0) {
      fullAttachments.push({ text: `${t('export_pdf.attachments')}:`, style: 'header' })
      fullAttachments = [fullAttachments, ...attachments]
    }
    var ifSection = renderIf(fullAnswerForPDF?.miniDyi!);
    //console.log("ifSection", ifSection)
    // var questionsContent: any[] = renderQuestions(fullAnswerForPDF?.sectionsForPDF);
    // var questionsContent1: any[] = renderQuestions(questionsJSON.pages[0].elements);
    // var questionsContent2: any[] = renderQuestions(questionsJSON.pages[0].elements);

    const docDefinition: TDocumentDefinitions = {
      pageSize: 'A4',
      pageMargins: [20, 20, 20, 20],
      defaultStyle: { fontSize: 12 },

      styles: {
        callheader: {
          fontSize: 18,
          bold: true,
          lineHeight: 1
        },
        blankLine: {
          fontSize: 12,
          lineHeight: 1.5 // Adjust this value to increase or decrease the space
        },
        header: {
          fontSize: 16,
          bold: true,
          marginTop: 15,
          marginBottom: 10,
        },
        header1: {
          fontSize: 12,
          bold: true
        },
        question: {
          bold: true,
          fontSize: 10,
          marginRight: 5,
          marginTop: 5,


          // border: [true, true, true, true]
        },
        answer: {
          marginTop: 5,
          fontSize: 10,
          color: '#333',
          italics: true,

        },
        footer: {
          marginRight: 15,
          fontSize: 8,
          color: '#333',
          italics: true,
        },
        footerLeft: {
          marginLeft: 15,
          fontSize: 8,
          color: '#333'
        },
        tableHeader: {
          bold: true,
          fontSize: 10,
          // fillColor: '#f2f2f2',
          alignment: 'center',
          // border: [true, true, true, true] // Top, right, bottom, left
        },
        tableHeaderRow: {
          bold: true,
          fontSize: 10, alignment: 'left',
        },
        tableCell: {
          fontSize: 10,
          alignment: 'center',
          // border: [true, true, true, true] // Top, right, bottom, left
        }
      },

      content: [

        { image: dataURL, width: fullAnswerForPDF?.configLogoWidth ?? 50, height: fullAnswerForPDF?.configLogoHeight ?? 50 }, // Add logo here
        { text: fullAnswerForPDF?.callName ?? "Call", style: 'callheader' },
        { text: ' ', style: 'blankLine' },
        { text: `${t('export_pdf.submitted_by')}: ${fullAnswerForPDF?.submittedBy}`, style: 'blankLine' },
        { text: `${t('export_pdf.submitted_when')}: ${formatSubmissionDateTime(fullAnswerForPDF?.submissionDateTime)}`, style: 'blankLine' },
        { text: `${t('export_pdf.code')}: ${fullAnswerForPDF?.submissionId}`, style: 'blankLine' },
        { text: `${t('export_pdf.general_info')}`, style: 'header' },
        {
          columns: [
            { text: `${t('export_pdf.org_name')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.orgName}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.p_iva')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.pIva}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.address')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.address}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.city')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.city}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.country')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.country}`, style: 'answer', width: '40%' }
          ]
        },
        { text: `${t('export_pdf.reference')}`, style: 'header' },
        {
          columns: [
            { text: `${t('export_pdf.ref_name')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.userReferenteNome}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.ref_last_name')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.userReferenteCognome}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.ref_role')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.userReferenteRuolo}`, style: 'answer', width: '40%' }
          ]
        },
        {
          columns: [
            { text: `${t('export_pdf.ref_email')}:`, style: 'question', width: '60%' },
            { text: `${fullAnswerForPDF?.userReferenteEmail}`, style: 'answer', width: '40%' }
          ]
        },
        { canvas: [{ type: 'line', x1: 0, y1: 5, x2: 550, y2: 5 }] },

        ...all ? all.map((e) => { console.log(e); return e ? ([{ text: `${t('export_pdf.section')}: ${e.title}`, style: 'header' }, e.questions]) : []; }) : [],
        ...ifSection,
        // { text: `Allegati:`, style: 'header' },
        ...fullAttachments!,

      ],
      footer(currentPage, pageCount) {
        return [
          {
            columns: [
              { text: `Impact Call by Open Impact S.r.L.`, alignment: 'left', style: "footerLeft" },
              { text: `${t('export_pdf.page')} ${currentPage.toString()} ${t('export_pdf.page_of')} ${pageCount}`, alignment: 'right', style: "footer" },
            ]
          }
        ];
      }
    };

    pdfMake.createPdf(docDefinition).download('questions_answers.pdf');
  };

  return (
    <>
      <Button
        variant="light"
        color={"blue"}
        mt="sm"
        radius="sm"
        style={{ float: "right" }}
        onClick={generatePDF}>Download PDF</Button>
    </>

  );
};

/**
 * <Button
              variant="light"
              color={"blue"}
              mt="sm"
              radius="sm"
              style={{ float: "right" }}
              onClick={() => {
                downloadFile(
                  `/api/FileUpload/GetFile?downloadId=${call.customFile ?.id}`,
                  call.customFile?.name!
                )}}
            >
 */

export default AnswerPdf;